import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormArray, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import * as ClientConstants from '../../../core/constants/client.constants';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { FileUploadModule } from 'primeng/fileupload';
import { faFilePlus } from '@fortawesome/pro-light-svg-icons';
import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { environment } from '../../../../environments/environment';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { IbanFormatPipe } from '../../../core/pipes/iban-format.pipe';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';

interface UploadEvent {
  originalEvent: Event;
  files: File[];
}

@Component({
  selector: 'app-labor-data-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, DropdownModule, InputTextModule, CheckboxModule, CalendarModule, AccordionModule, CommonModule, FileUploadModule, FontAwesomeModule, IbanFormatPipe, AutoAuditDirective],
  templateUrl: './labor-data-form.component.html',
  styleUrl: './labor-data-form.component.scss'
})


export class LaborDataFormComponent implements OnChanges {

  private _clientId: any;
  
  @Input() 
  set clientId(value: any) {
    if (this._clientId !== value) {
      this._clientId = value;
      this.getData();
    }
  }
  
  get clientId(): any {
    return this._clientId;
  }
  
  @Input() snapshotData: any | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();
  public clientConstants = ClientConstants;

  faFilePlus = faFilePlus;
  faFloppyDisk = faFloppyDisk;

  fieldAuditData: LatestPerFieldMap = {};

  laborDataForm = this.fb.group({
    id: [],
    REDSystemNumber: [''],
    socialSecurityRegime: [''],
    companyLogo: [''],
    payrollDomiciliationIban: [''],
    socialInsuranceIban: [''],
    mutualInsuranceCompany: [''],
    collectiveAgreement: [''],
    client: [],
  });

  workCentersForm = this.fb.group({
    workCenters: this.fb.array([])
  });

  bankAccounts: any[] = [];

  uploadedLogo: File = null;
  uploadedLogoUrl: string;

  uploadedSignature: File = null;
  uploadedSignatureUrl: string;

  isSaved: boolean = false;
  @ViewChild('progressBar', { static: false }) progressBar!: ElementRef<HTMLProgressElement>;

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private auditService: AuditService,
    ) {
      this.laborDataForm.valueChanges.subscribe(() => {
        if (this.laborDataForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
      this.workCentersForm.valueChanges.subscribe(() => {
        if (this.workCentersForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });
     }

  async ngOnInit() {
    if (!this.snapshotData) {
      await this.getData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        this.laborDataForm.patchValue(this.snapshotData, { emitEvent: false });
        this.laborDataForm.disable({ emitEvent: false });
      } else if (!changes['snapshotData'].firstChange) {
        this.laborDataForm.enable({ emitEvent: false });
        void this.getData();
      }
    }
  }

  async getData() {
    if (this.clientId) {
      // Limpiar arrays antes de recargar
      let workCentersArray = this.workCentersForm.get('workCenters') as FormArray;
      while (workCentersArray.length !== 0) {
        workCentersArray.removeAt(0);
      }

      this.bankAccounts = (await this.clientsService.getClientBankAccounts(this.clientId))
        .filter((account: any) => account.endDate === null);
      
      let data = await this.clientsService.getClientLabor(this.clientId);
      
      if (data) {
        this.laborDataForm.patchValue(data);
        
        // Construir URL completa del logotipo si existe
        if (data.companyLogo) {
          this.uploadedLogoUrl = this.buildImageUrl(data.companyLogo);
        } else {
          this.uploadedLogoUrl = null;
        }
        
        // Construir URL completa de la firma si existe
        if (data.companySignature) {
          this.uploadedSignatureUrl = this.buildImageUrl(data.companySignature);
        } else {
          this.uploadedSignatureUrl = null;
        }
      } else {
        // Si no hay datos, limpiar las URLs
        this.uploadedLogoUrl = null;
        this.uploadedSignatureUrl = null;
      }
      
      let workCenters = await this.clientsService.getClientWorkCenters(this.clientId);
      workCenters.forEach(center => {
        center.startDate = center.startDate ? new Date(center.startDate) : null;
        center.endDate = center.endDate ? new Date(center.endDate) : null;
        workCentersArray.push(this.fb.group(center));
      });

      try {
        this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_labor');
      } catch {
        this.fieldAuditData = {};
      }
    } else {
      // Limpiar datos si no hay clientId
      this.uploadedLogoUrl = null;
      this.uploadedSignatureUrl = null;
      let workCentersArray = this.workCentersForm.get('workCenters') as FormArray;
      while (workCentersArray.length !== 0) {
        workCentersArray.removeAt(0);
      }
    }
  }

  private buildImageUrl(url: string): string {
    if (!url) return null;
    
    // Si ya es una URL completa (http:// o https://), devolverla tal cual
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // Si es una ruta relativa, construir la URL completa con el base URL del API
    // Remover el prefijo /api si existe en la URL
    const cleanUrl = url.startsWith('/api/') ? url.substring(5) : url.startsWith('/') ? url.substring(1) : url;
    return `${environment.apiUrl}/${cleanUrl}`;
  }

  get workCenters(): FormArray {
    return this.workCentersForm.get('workCenters') as FormArray;
  }

  addWorkCenter() {
    const workCenter = this.fb.group({
      startDate: [],
      principal: [''],
      workCenterQuoteAccountCode: [''],
      unpaidInternshipQuoteAccountCode: [''],
      laborAuthorityRegistrationNumber: [''],
      mutualInsuranceCompany: [''],
      collectiveAgreement: [''],
      sprl: [''],
      agreementInsurance: [''],
      representation: [''],
      personnelDelegates: [''],
      worksCouncil: [''],
      bankAccount: [''],
      payroll: [''],
      socialInsurance: [''],
      irpf: [''],
      acronym: [''],
      street: [''],
      number: [''],
      stairs: [''],
      floor: [''],
      door: [''],
      town: [''],
      postalCode: [''],
      province: [''],
      codeCountry: [''],
      country: [''],
      cadastralReference: [''],
      personnel: [''],
      codeCNAE: [''],
      codeIAE: [''],
      activityType: [''],
      activity: [''],
      endDate: [],
    });

    this.workCenters.push(workCenter);
  }

  onUploadLogo(event) {
      this.uploadedLogo = event.currentFiles[0];
      // Crear vista previa del archivo seleccionado
      if (this.uploadedLogo) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.uploadedLogoUrl = e.target.result;
        };
        reader.readAsDataURL(this.uploadedLogo);
      }
  }

  onRemoveLogo() {
      this.uploadedLogo = null;
      // Recargar datos para restaurar la URL del servidor si existe
      this.getData();
  }

  onUploadSignature(event) {
      this.uploadedSignature = event.currentFiles[0];
      // Crear vista previa del archivo seleccionado
      if (this.uploadedSignature) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.uploadedSignatureUrl = e.target.result;
        };
        reader.readAsDataURL(this.uploadedSignature);
      }
  }

  onRemoveSignature() {
      this.uploadedSignature = null;
      // Recargar datos para restaurar la URL del servidor si existe
      this.getData();
  }

  async save() {
    
    if (!this.laborDataForm.value.client) {
      this.laborDataForm.value.client = Number(this.clientId);
    }
    await this.clientsService.saveClientLabor(this.laborDataForm.value);

    for (let i = 0; i < this.workCenters.length; i++) {
      if (this.workCenters.at(i).dirty) {
        if(!this.workCenters.at(i).value.client) {
          this.workCenters.at(i).value.client = Number(this.clientId);
  
        }
        if (!this.workCenters.at(i).value.id) {
          delete this.workCentersForm.value['id'];
        }
        await this.clientsService.saveClientWorkCenter(this.workCenters.at(i).value);
      }
    }

    let labor = await this.clientsService.getClientLabor(this.clientId);
    if (this.uploadedLogo) {
      await this.clientsService.uploadCompanyLogo(this.uploadedLogo, labor.id);
    }
    if (this.uploadedSignature) {
      await this.clientsService.uploadCompanySignature(this.uploadedSignature, labor.id);
    }
    this.workCenters.clear();
    await this.getData();

    this.isSaved = true;
  }

  choose(event, callback) {
    callback();
}

}
